import Loading from '@/components/Loading';
import { useEffect, useState } from 'react';
import { LuPencil, LuTrash2 } from 'react-icons/lu';
import clsx from 'clsx';
import type { AdminApiOrganizationUser } from '@/types/serializers';
import { AdminButton } from '../../components/AdminButton';
import useFetchAdminOrganization from '../../api/useFetchAdminOrganization';
import useFetchAdminOrganizationStaffs from '../../api/useFetchAdminOrganizationStaffs';
import useFetchAdminIam from '../../api/useFetchAdminIam';
import AdminOrganizationEditModal from '../../components/organisms/AdminOrganizationEditModal';
import AdminOrganizationPlanEditModal from '../../components/organisms/AdminOrganizationPlanEditModal';
import AdminOrganizationDeleteModal from '../../components/organisms/AdminOrganizationDeleteModal';
import AdminOrganizationStaffModal from '../../components/organisms/AdminOrganizationStaffModal';
import AdminBackLink from '../../components/atoms/AdminBackLink';
import AdminPlanTag from '../../components/atoms/AdminPlanTag';
import AdminProgressBar from '../../components/atoms/AdminProgressBar';
import AdminRoleBadge from '../../components/atoms/AdminRoleBadge';
import AdminLoadingState from '../../components/molecules/AdminLoadingState';
import AdminErrorState from '../../components/molecules/AdminErrorState';
import {
  formatDate,
  formatDateTime,
  getRemainingDaysLabel,
  getPlanLabel,
  getProgressValue,
} from '../../utils/formatters';

type AdminOrganizationDetailPageProps = {
  organizationId: number;
};

const getStaffRoleLabel = (isAdmin: boolean, isSupport: boolean) => {
  if (isAdmin) {
    return { label: '管理者', color: 'bg-[#3b82f6]' };
  }
  if (isSupport) {
    return { label: 'サポート', color: 'bg-[#f59e0b]' };
  }
  return { label: '通常', color: 'bg-[#6be06e]' };
};

// テーブル列構成。super_admin専用列はモーダルに集約したため admin/super_admin で共通の4列
const GRID_TEMPLATE_COLUMNS = '220px 1fr 140px 200px';
const TABLE_HEADERS = ['氏名', 'メールアドレス', '権限', 'ステータス'];

export default function AdminOrganizationDetailPage({
  organizationId,
}: AdminOrganizationDetailPageProps) {
  const { data: currentUser } = useFetchAdminIam();
  const { data, isLoading, isError } = useFetchAdminOrganization(organizationId);
  const {
    data: staffList = [],
    isLoading: isStaffLoading,
    isError: isStaffError,
  } = useFetchAdminOrganizationStaffs(organizationId);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPlanEditOpen, setIsPlanEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<AdminApiOrganizationUser | null>(null);
  const isSuperAdmin = currentUser?.role === 'super_admin';

  // staffList が再フェッチされたとき、選択中スタッフを最新版に差し替える
  // is_support の更新で can_impersonate も連動するため、モーダル側に最新データを伝える必要がある
  // 依存配列は selectedStaff?.id に絞る。selectedStaff オブジェクト全体を入れると
  // setSelectedStaff(updated) で参照が変わるたびに effect が再発火するため
  useEffect(() => {
    if (!selectedStaff) return;
    const updated = staffList.find((s) => s.id === selectedStaff.id);
    if (!updated) {
      setSelectedStaff(null);
      return;
    }
    if (updated !== selectedStaff) {
      setSelectedStaff(updated);
    }
  }, [staffList, selectedStaff?.id]);

  if (isLoading) return <AdminLoadingState />;
  if (isError || !data)
    return <AdminErrorState message="クライアント詳細の読み込みに失敗しました。" />;

  const progress = getProgressValue(data);

  return (
    <div className="flex max-w-[1143px] flex-col gap-6">
      <AdminBackLink to="/admin" search={{ tab: 'organizations' }} />
      <div className="rounded bg-white">
        <div className="flex gap-12">
          <div className="flex w-[698px] flex-col gap-8">
            <div className="pb-2">
              <div className="flex items-center gap-8">
                <h1 className="text-2xl font-bold text-main">{data.name}</h1>
                {isSuperAdmin && (
                  <div className="flex items-center gap-2">
                    <AdminButton
                      label="編集する"
                      icon={<LuPencil className="size-4" />}
                      onClick={() => setIsEditOpen(true)}
                    />
                    <AdminButton
                      label="削除する"
                      icon={<LuTrash2 className="size-4" />}
                      variant="danger"
                      onClick={() => setIsDeleteOpen(true)}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {[
                { label: '登録エージェント数', value: String(data.agent_count), unit: '名' },
                { label: '登録求職者数', value: String(data.candidate_profile_count), unit: '名' },
                { label: '登録求人数', value: String(data.provided_job_post_count), unit: '件' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex w-[150px] flex-col items-center justify-center rounded-lg bg-gray px-4 pt-3 pb-2"
                >
                  <div className="text-sm font-bold text-[#4188ff]">{item.label}</div>
                  <div className="flex items-end gap-1">
                    <span className="text-3xl font-bold text-main">{item.value}</span>
                    <span className="pb-1 text-sm font-bold text-main">{item.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex w-[301px] flex-col gap-6 rounded-lg bg-white p-4 shadow-[2px_2px_8px_0px_rgba(0,0,0,0.25)]">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-3">
                <span className="text-xs font-bold text-[#777]">現在のプラン</span>
                <AdminPlanTag plan={getPlanLabel(data)} planType={data.plan_type} size="lg" />
              </div>
              {isSuperAdmin && (
                <AdminButton label="編集" size="sm" onClick={() => setIsPlanEditOpen(true)} />
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <AdminProgressBar value={progress} />
              <span className="text-xs text-[#777]">
                {getRemainingDaysLabel(data.contract_end_date)}
              </span>
            </div>
            <div className="flex flex-col gap-2 text-xs text-[#333]">
              <div className="flex gap-4">
                <span className="font-bold">契約期間</span>
                <span>
                  {formatDate(data.contract_start_date)} ~ {formatDate(data.contract_end_date)}
                </span>
              </div>
              <div className="flex gap-4">
                <span className="font-bold">最終ログイン</span>
                <span>{formatDateTime(data.staff_last_sign_in_at)}</span>
              </div>
              <div className="flex gap-4">
                <span className="font-bold">マイページログイン</span>
                <span>{data.is_portal_login_enabled ? 'ON' : 'OFF'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded bg-white">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-main">スタッフ一覧</h2>
          <span className="text-xs text-sub">合計 {staffList.length} 名</span>
        </div>
        <div className="mt-4 overflow-hidden rounded border border-solid border-border bg-white">
          <div className="grid bg-[#f3f7fe]" style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}>
            {TABLE_HEADERS.map((header) => (
              <div
                key={header}
                className="flex items-center px-6 py-2 text-xs font-bold text-black"
              >
                {header}
              </div>
            ))}
          </div>
          <div className="flex flex-col">
            {isStaffLoading && (
              <div className="flex items-center justify-center py-8">
                <Loading />
              </div>
            )}
            {isStaffError && !isStaffLoading && (
              <div className="py-8 text-center">
                <p className="text-sm text-sub">スタッフ一覧の取得に失敗しました。</p>
              </div>
            )}
            {!isStaffLoading && !isStaffError && staffList.length === 0 && (
              <div className="py-10 text-center">
                <p className="text-sm text-sub">スタッフが登録されていません。</p>
              </div>
            )}
            {!isStaffLoading &&
              !isStaffError &&
              staffList.map((staff, index) => {
                const role = getStaffRoleLabel(staff.is_admin, staff.is_support);
                const isLast = index === staffList.length - 1;
                const handleRowClick = () => {
                  if (isSuperAdmin) setSelectedStaff(staff);
                };
                return (
                  <div
                    key={staff.id}
                    role={isSuperAdmin ? 'button' : undefined}
                    tabIndex={isSuperAdmin ? 0 : undefined}
                    onClick={handleRowClick}
                    onKeyDown={(e) => {
                      if (!isSuperAdmin) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedStaff(staff);
                      }
                    }}
                    className={clsx(
                      'grid border-0 border-b border-solid border-[#ddd] text-sm',
                      isLast && 'border-b-0',
                      isSuperAdmin && 'cursor-pointer hover:bg-[#f9fafb]'
                    )}
                    style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                  >
                    <div className="flex items-center gap-3 px-6 py-3">
                      <img
                        src={staff.avatar_url}
                        alt={staff.name}
                        className="size-8 rounded-full object-cover"
                      />
                      <span className="font-bold text-black">{staff.name}</span>
                    </div>
                    <div className="flex min-w-0 items-center px-6 py-3 text-xs text-black">
                      <span className="block w-full truncate">{staff.email}</span>
                    </div>
                    <div className="flex items-center px-6 py-3">
                      <AdminRoleBadge label={role.label} color={role.color} />
                    </div>
                    <div className="flex items-center gap-2 px-6 py-3 text-xs text-black">
                      <span>{staff.status}</span>
                      {staff.is_support && (
                        <span className="rounded bg-[#f59e0b]/20 px-2 py-0.5 text-xs whitespace-nowrap text-[#b45309]">
                          サポートアカウント
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
      {isSuperAdmin && (
        <AdminOrganizationEditModal
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          organization={data}
        />
      )}
      {isSuperAdmin && (
        <AdminOrganizationPlanEditModal
          open={isPlanEditOpen}
          onClose={() => setIsPlanEditOpen(false)}
          organization={data}
        />
      )}
      {isSuperAdmin && (
        <AdminOrganizationDeleteModal
          open={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          organizationId={data.id}
          organizationName={data.name}
          onDeleted={() => {
            window.location.href = '/admin?tab=organizations';
          }}
        />
      )}
      {isSuperAdmin && (
        <AdminOrganizationStaffModal
          open={selectedStaff !== null}
          onClose={() => setSelectedStaff(null)}
          staff={selectedStaff}
          organizationId={organizationId}
        />
      )}
    </div>
  );
}
